import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SOCIAL_LABELS, SOCIAL_POST_LIMITS, SOCIAL_PROVIDERS, type IntegrationSummary, type SocialPost, type SocialProvider } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './marketing.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/**
 * Marketing → Beiträge (KON-193): ein Beitrag geht sofort an die gewählten Netzwerke. Facebook und LinkedIn bekommen Text und Link, TikTok ein Video von einer Internetadresse.
 * Welche Netzwerke zur Wahl stehen, hängt davon ab, was der Mandant unter „APIs und Webhooks“ verbunden hat.
 */
export function PostsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [chosen, setChosen] = useState<Set<SocialProvider>>(new Set());
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const mayPublish = can('marketing.send');
  const posts = useQuery({ queryKey: ['social-posts'], queryFn: async () => (await api<{ data: SocialPost[] }>('GET', '/marketing/posts')).data });
  // Die Übersicht der Verbindungen braucht das Recht „APIs und Webhooks“. Ohne es zeigt die Seite alle Netzwerke und der Server meldet, was fehlt.
  const integrations = useQuery({ queryKey: ['integrations'], queryFn: async () => (await api<{ data: IntegrationSummary[] }>('GET', '/integrations')).data, enabled: can('setup.integrations'), retry: false });
  const connected = (p: SocialProvider) => integrations.data?.find((i) => i.key === p)?.status === 'connected';

  const publish = useMutation({
    mutationFn: async () => (await api<{ data: SocialPost }>('POST', '/marketing/posts', { text: text || undefined, link: link || undefined, videoUrl: chosen.has('tiktok') ? videoUrl || undefined : undefined, providers: [...chosen] })).data,
    onSuccess: (post) => {
      client.invalidateQueries({ queryKey: ['social-posts'] });
      const failed = post.targets.filter((t) => t.status === 'failed');
      setMessage(failed.length === 0 ? { ok: true, text: 'Der Beitrag ist veröffentlicht.' } : { ok: false, text: `Nicht überall hat es geklappt: ${failed.map((t) => `${SOCIAL_LABELS[t.provider]} (${t.error})`).join(' ')}` });
      if (failed.length === 0) {
        setText('');
        setLink('');
        setVideoUrl('');
        setChosen(new Set());
      }
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Veröffentlichen hat nicht geklappt.') }),
  });
  const retry = useMutation({
    mutationFn: (id: string) => api('POST', `/marketing/posts/${id}/retry`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['social-posts'] }),
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Wiederholung hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/marketing/posts/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['social-posts'] }),
  });

  const toggle = (p: SocialProvider) =>
    setChosen((c) => {
      const next = new Set(c);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    publish.mutate();
  };

  return (
    <>
      <div className="page-head">
        <h1>Beiträge</h1>
      </div>
      <p className="muted">Veröffentlichen Sie einen Beitrag auf Ihren verbundenen Seiten. Die Netzwerke verbinden Sie unter <Link to="/einrichtung/api">Einrichtung → APIs und Webhooks</Link>.</p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}

      {mayPublish && (
        <form className="card" onSubmit={submit} noValidate>
          <h2>Neuer Beitrag</h2>
          <fieldset className="field">
            <legend><span>Wohin?</span></legend>
            {SOCIAL_PROVIDERS.map((p) => (
              <label key={p} className="check">
                <input type="checkbox" checked={chosen.has(p)} onChange={() => toggle(p)} />
                <span>{SOCIAL_LABELS[p]}{integrations.data && !connected(p) ? <span className="muted small"> (noch nicht verbunden)</span> : null}</span>
              </label>
            ))}
          </fieldset>
          <label className="field">
            <span>Text</span>
            <textarea className="input" rows={5} value={text} onChange={(e) => setText(e.target.value)} maxLength={SOCIAL_POST_LIMITS.text} />
            <small className="muted">{text.length} von {SOCIAL_POST_LIMITS.text} Zeichen. Bei TikTok ist es der Titel des Videos.</small>
          </label>
          <label className="field"><span>Link (freiwillig, Facebook und LinkedIn)</span><input className="input" type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" /></label>
          {chosen.has('tiktok') && <label className="field"><span>Adresse des Videos (TikTok)</span><input className="input" type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://…/video.mp4" /><small className="muted">TikTok holt das Video selbst ab. Die Adresse muss bei TikTok als Domain bestätigt sein.</small></label>}
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={publish.isPending || chosen.size === 0}>{publish.isPending ? 'Wird veröffentlicht …' : 'Jetzt veröffentlichen'}</button>
          </div>
        </form>
      )}

      <h2>Bisherige Beiträge</h2>
      {posts.isLoading && <p className="muted">Wird geladen …</p>}
      {posts.data?.length === 0 && <p className="muted">Noch keine Beiträge.</p>}
      <ul className="post-list">
        {posts.data?.map((p) => (
          <li key={p.id} className="card post">
            <p className="post__text">{p.text ?? p.videoUrl ?? p.link}</p>
            {p.link && p.text && <p className="small muted">{p.link}</p>}
            <ul className="post__targets small">
              {p.targets.map((t) => (
                <li key={t.provider} className={t.status === 'failed' ? 'post__failed' : ''}>
                  <strong>{SOCIAL_LABELS[t.provider]}</strong>: {t.status === 'published' ? 'veröffentlicht' : `nicht geklappt. ${t.error ?? ''}`}
                </li>
              ))}
            </ul>
            <p className="small muted">{stamp.format(new Date(p.createdAt))}{p.createdByName ? ` · ${p.createdByName}` : ''}</p>
            {mayPublish && (
              <div className="form-actions">
                {p.targets.some((t) => t.status === 'failed') && <button className="btn btn--secondary btn--sm" type="button" disabled={retry.isPending} onClick={() => retry.mutate(p.id)}>Gescheiterte wiederholen</button>}
                <button className="btn btn--secondary btn--sm" type="button" onClick={() => { if (window.confirm('Den Eintrag aus der Liste löschen? Der Beitrag bleibt im Netzwerk bestehen.')) remove.mutate(p.id); }}>Aus der Liste löschen</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
